import React, { useEffect, Suspense, useState } from 'react';
import { Outlet, NavLink, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../features/auth/useAuth';
// import { useWebSocketContext } from '../contexts/WebSocketContext'; // Removed - using globalWebSocketManager instead
import { Alert } from '../components/ui/Alert';
import { Menu, X } from 'lucide-react';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { GlobalSearchModal } from '../components/search/GlobalSearchModal';

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

// Error boundary component
class DashboardErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // TODO: Send error to logging service
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-4">
          <Alert type="error">
            Something went wrong. Please try refreshing the page.
          </Alert>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading component
const DashboardLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
  </div>
);

const DashboardLayout: React.FC<DashboardLayoutProps> = () => {
  const { user, isLoading } = useAuth();
  // const { addMessageHandler } = useWebSocketContext(); // Removed - using globalWebSocketManager
  const location = useLocation();
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Keyboard shortcuts
  const { isSearchOpen, setIsSearchOpen } = useKeyboardShortcuts({
    onSearch: () => setIsSearchOpen(true),
    onEscape: () => {
      setIsSearchOpen(false);
      setIsMobileMenuOpen(false);
    },
  });

  // Force close search modal on mount to prevent stuck state
  useEffect(() => {
    setIsSearchOpen(false);
  }, []);

  // Redirect to login if not authenticated
  if (!isLoading && !user) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Show loading state
  if (isLoading) {
    return <DashboardLoading />;
  }

  // WebSocket connection is now handled by the globalWebSocketManager through hooks
  // No need for duplicate connection management here

  const getLinkClass = ({ isActive }: { isActive: boolean }) => {
    return `group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
      isActive 
        ? 'bg-accent text-accent-foreground' 
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`;
  };

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navigationItems = [
    { to: '/dashboard/services', label: t('dashboard.services') },
    { to: '/dashboard/plans', label: t('dashboard.plans') },
    { to: '/dashboard/billing', label: t('dashboard.billing') },
    { to: '/dashboard/settings', label: t('dashboard.settings') },
    { to: '/dashboard/activity', label: t('dashboard.activity') },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:underline"
      >
        Skip to main content
      </a>
      <Header />
      
      <DashboardErrorBoundary>
        <div className="flex-1 flex">
        {/* Mobile menu button - moved to top-left for better visibility */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden fixed top-20 left-4 z-50 p-2.5 bg-primary text-primary-foreground rounded-md shadow-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
          aria-label="Toggle navigation menu"
        >
          <Menu size={20} />
          <span className="text-sm font-medium">Menu</span>
        </button>

        {/* Mobile navigation overlay */}
        {isMobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/50"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}

        {/* Mobile navigation menu */}
        <aside
          className={`md:hidden fixed inset-y-0 left-0 z-40 w-64 bg-background border-r border-border transform transition-transform duration-300 ease-in-out ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="text-lg font-semibold">{t('dashboard.menu')}</h2>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 rounded-md hover:bg-muted"
                aria-label="Close menu"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {navigationItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={getLinkClass}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </aside>

        {/* Desktop Sidebar */}
        <aside className="hidden md:flex md:flex-shrink-0" role="navigation" aria-label="Main navigation">
          <div className="flex flex-col w-64 border-r border-border dark:bg-muted/50 bg-muted/30">
            <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
              <nav className="mt-5 flex-1 px-2 space-y-1" role="navigation" aria-label="Dashboard navigation">
                {navigationItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={getLinkClass}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </nav>
            </div>
          </div>
        </aside>

          {/* Main content */}
          <main id="main-content" className="flex-1 relative overflow-y-auto focus:outline-none bg-background" role="main" tabIndex={-1}>
            <div className="py-6">
              <div className="max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] 3xl:max-w-[1800px] mx-auto px-4 sm:px-6 md:px-8 transition-all">
                <Suspense fallback={<DashboardLoading />}>
                  <Outlet />
                </Suspense>
              </div>
            </div>
          </main>
        </div>
      </DashboardErrorBoundary>

      <Footer />
      
      {/* Global Search Modal */}
      <GlobalSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </div>
  );
};

export default DashboardLayout;
